import {ILogService, IQService, IWindowService} from "angular";
import * as restangular from "restangular";

/**
 * Push Notification Service
 *
 * Handles Web Push notifications for PWA standalone mode and
 * communication with native Capacitor app via webkit messageHandlers.
 */
export class PushNotificationService {

    public static $inject = ['$q', '$window', '$localStorage', '$log', 'Restangular'];

    private static PUSH_ENABLED_KEY = 'pushNotificationsEnabled';
    private static PUSH_PROMPT_DISMISSED_KEY = 'pushNotificationsPromptDismissed';

    private vapidPublicKey: string;

    constructor(
        private $q: IQService,
        private $window: IWindowService,
        private $localStorage: any,
        private $log: ILogService,
        private Rest: restangular.IService
    ) {
        // VAPID key will be configured in config.js
        this.vapidPublicKey = (this.$window as any).evmConfig?.vapidPublicKey || '';
    }

    /**
     * Check if running inside Capacitor InAppBrowser with native bridge
     * Supports both cordova_iab (legacy) and evmBridge handlers
     */
    public isInNativeApp(): boolean {
        return !!(this.$window as any).webkit?.messageHandlers?.cordova_iab ||
               !!(this.$window as any).webkit?.messageHandlers?.evmBridge;
    }

    /**
     * Get the native bridge message handler
     */
    private getNativeBridge(): any {
        const handlers = (this.$window as any).webkit?.messageHandlers;
        return handlers?.cordova_iab || handlers?.evmBridge;
    }

    /**
     * Check if PWA is running in standalone mode (installed)
     */
    public isPWAStandalone(): boolean {
        return (this.$window as any).matchMedia?.('(display-mode: standalone)').matches ||
               (this.$window.navigator as any).standalone === true;
    }

    /**
     * Check if Web Push API is supported
     */
    public isSupported(): boolean {
        return 'serviceWorker' in this.$window.navigator &&
               'PushManager' in this.$window &&
               'Notification' in this.$window;
    }

    public getNotificationPermission(): string {
        if (!('Notification' in this.$window)) {
            return 'unsupported';
        }
        return (this.$window as any).Notification.permission || 'default';
    }

    public isPushPreferenceEnabled(): boolean {
        return this.$localStorage[PushNotificationService.PUSH_ENABLED_KEY] !== false;
    }

    public shouldAutoSubscribe(): boolean {
        return !this.isInNativeApp()
            && this.isSupported()
            && this.isPushPreferenceEnabled()
            && this.getNotificationPermission() === 'granted';
    }

    public shouldShowActivationPrompt(): boolean {
        return !this.isInNativeApp()
            && this.isSupported()
            && this.isPushPreferenceEnabled()
            && !this.$localStorage[PushNotificationService.PUSH_PROMPT_DISMISSED_KEY]
            && this.getNotificationPermission() === 'default';
    }

    public dismissActivationPrompt(): void {
        this.$localStorage[PushNotificationService.PUSH_PROMPT_DISMISSED_KEY] = true;
    }

    public canManagePushNotifications(): boolean {
        return this.isInNativeApp() || this.isSupported();
    }

    public isPushBlocked(): boolean {
        if (this.isInNativeApp()) {
            return false;
        }
        return this.getNotificationPermission() === 'denied';
    }

    public getSettingsEnabled(): boolean {
        return this.isPushPreferenceEnabled();
    }

    public setPushPreferenceEnabled(enabled: boolean): boolean {
        this.$localStorage[PushNotificationService.PUSH_ENABLED_KEY] = enabled;
        if (!enabled) {
            this.$localStorage[PushNotificationService.PUSH_PROMPT_DISMISSED_KEY] = true;
        }
        return enabled;
    }

    /**
     * Notify native Capacitor app about successful authentication
     * This allows the native app to register for native push notifications
     */
    public notifyNativeAppAuth(authToken: string): void {
        if (this.isInNativeApp()) {
            try {
                const bridge = this.getNativeBridge();
                // Native app expects JSON string, not object
                bridge.postMessage(JSON.stringify({
                    action: 'userAuthenticated',
                    authToken: authToken
                }));
                this.$log.info('PushNotificationService: Notified native app about authentication');
            } catch (e) {
                this.$log.error('PushNotificationService: Failed to notify native app:', e);
            }
        }
    }

    /**
     * Notify native Capacitor app about logout
     * This allows the native app to unregister push notifications
     */
    public notifyNativeAppLogout(): void {
        if (this.isInNativeApp()) {
            try {
                const bridge = this.getNativeBridge();
                // Native app expects JSON string, not object
                bridge.postMessage(JSON.stringify({
                    action: 'userLoggedOut'
                }));
                this.$log.info('PushNotificationService: Notified native app about logout');
            } catch (e) {
                this.$log.error('PushNotificationService: Failed to notify native app about logout:', e);
            }
        }
    }

    /**
     * Subscribe to Web Push notifications
     * Only works in PWA standalone mode when not in native app
     */
    public subscribeToPush(): angular.IPromise<void> {
        const deferred = this.$q.defer<void>();

        if (!this.isSupported()) {
            this.$log.warn('PushNotificationService: Push notifications not supported');
            deferred.reject('Push not supported');
            return deferred.promise;
        }

        if (this.getNotificationPermission() !== 'granted') {
            this.$log.warn('PushNotificationService: Notification permission not granted');
            deferred.reject('Notification permission not granted');
            return deferred.promise;
        }

        if (!this.vapidPublicKey) {
            this.$log.warn('PushNotificationService: No VAPID public key configured');
            deferred.reject('No VAPID key');
            return deferred.promise;
        }

        // Don't subscribe if in native app (native app handles push separately)
        if (this.isInNativeApp()) {
            this.$log.info('PushNotificationService: In native app, skipping web push subscription');
            deferred.resolve();
            return deferred.promise;
        }

        const self = this;

        this.$window.navigator.serviceWorker.ready.then(function(registration: ServiceWorkerRegistration) {
            return registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: self.urlBase64ToUint8Array(self.vapidPublicKey)
            });
        }).then(function(subscription: PushSubscription) {
            const keys = subscription.toJSON().keys || {};

            // Send subscription to backend
            return self.Rest.all('push/subscriptions/web').post({
                endpoint: subscription.endpoint,
                authKey: keys.auth,
                p256dhKey: keys.p256dh
            });
        }).then(function() {
            self.$log.info('PushNotificationService: Web push subscription registered');
            deferred.resolve();
        }).catch(function(error: any) {
            self.$log.error('PushNotificationService: Push subscription failed:', error);
            deferred.reject(error);
        });

        return deferred.promise;
    }

    public enablePushNotifications(authToken?: string): angular.IPromise<void> {
        const deferred = this.$q.defer<void>();
        this.$localStorage[PushNotificationService.PUSH_PROMPT_DISMISSED_KEY] = true;
        this.setPushPreferenceEnabled(true);

        if (this.isInNativeApp()) {
            if (!authToken) {
                deferred.reject('Missing auth token');
                return deferred.promise;
            }
            this.notifyNativeAppAuth(authToken);
            deferred.resolve();
            return deferred.promise;
        }

        if (!this.isSupported()) {
            deferred.reject('Push not supported');
            return deferred.promise;
        }

        const notificationApi = (this.$window as any).Notification;
        const permission = this.getNotificationPermission();
        const self = this;

        let permissionHandled = false;
        const subscribeAfterPermission = function(result: string) {
            if (permissionHandled) {
                return;
            }
            permissionHandled = true;

            if (result !== 'granted') {
                deferred.reject('Notification permission not granted');
                return;
            }
            self.subscribeToPush().then(function() {
                deferred.resolve();
            }, function(error) {
                deferred.reject(error);
            });
        };

        if (permission === 'granted') {
            subscribeAfterPermission(permission);
        } else if (permission === 'default') {
            const requestResult = notificationApi.requestPermission(subscribeAfterPermission);
            if (requestResult && requestResult.then) {
                requestResult.then(subscribeAfterPermission);
            }
        } else {
            deferred.reject('Notification permission denied');
        }

        return deferred.promise;
    }

    public disablePushNotifications(): angular.IPromise<void> {
        this.$localStorage[PushNotificationService.PUSH_PROMPT_DISMISSED_KEY] = true;
        this.setPushPreferenceEnabled(false);
        return this.$q.when();
    }

    /**
     * Unsubscribe from Web Push notifications
     */
    public unsubscribe(): angular.IPromise<void> {
        const deferred = this.$q.defer<void>();

        if (!this.isSupported()) {
            deferred.resolve();
            return deferred.promise;
        }

        const self = this;

        this.$window.navigator.serviceWorker.ready.then(function(registration: ServiceWorkerRegistration) {
            return registration.pushManager.getSubscription();
        }).then(function(subscription: PushSubscription | null) {
            if (subscription) {
                // Unregister from backend first, then unsubscribe locally
                return self.Rest.all('push/subscriptions/web')
                    .customDELETE('', { endpoint: subscription.endpoint })
                    .then(function() {
                        return subscription.unsubscribe();
                    });
            }
            return Promise.resolve(true);
        }).then(function() {
            self.$log.info('PushNotificationService: Push unsubscribed');
            deferred.resolve();
        }).catch(function(error: any) {
            self.$log.error('PushNotificationService: Push unsubscribe failed:', error);
            deferred.reject(error);
        });

        return deferred.promise;
    }

    /**
     * Convert URL-safe base64 VAPID key to Uint8Array
     * Required format for applicationServerKey in pushManager.subscribe()
     */
    private urlBase64ToUint8Array(base64String: string): Uint8Array {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
            .replace(/-/g, '+')
            .replace(/_/g, '/');

        const rawData = this.$window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    }
}
